<template>
  <div class="refund-view-page pa-4 pa-md-6">
    <div class="page-container">
      <!-- Breadcrumb & Top Bar -->
      <div class="d-flex flex-wrap align-center justify-space-between mb-4">
        <div class="d-flex align-center">
          <v-btn
            icon
            class="me-2"
            color="#082C69"
            @click="$router.push('/customer-service/refunds')"
          >
            <v-icon size="24">mdi-arrow-right</v-icon>
          </v-btn>
          <div>
            <div class="d-flex align-center gap-1 text-caption text-muted mb-1">
              <span class="breadcrumb-link" @click="$router.push('/customer-service')">الرئيسية</span>
              <v-icon x-small color="#94A3B8">mdi-chevron-left</v-icon>
              <span class="breadcrumb-link" @click="$router.push('/customer-service/refunds')">طلبات الاسترداد</span>
              <v-icon x-small color="#94A3B8">mdi-chevron-left</v-icon>
              <span class="navy--text font-weight-medium">تفاصيل الطلب</span>
            </div>
            <h1 class="text-h5 font-weight-bold navy--text mb-0 d-flex align-center">
              <span>طلب استرداد</span>
              <span class="mono-code ms-2 text-sky">{{ refund.request_code || '...' }}</span>
            </h1>
          </div>
        </div>

        <!-- Top Actions Bar -->
        <div class="d-flex align-center gap-2 mt-2 mt-sm-0">
          <v-btn
            outlined
            color="#64748B"
            class="rounded-lg font-weight-bold"
            @click="$router.push('/customer-service/refunds')"
          >
            <v-icon left size="18">mdi-arrow-left</v-icon>
            رجوع
          </v-btn>

          <!-- Start Review Button -->
          <v-btn
            v-if="refund.can_start_review"
            color="#082C69"
            class="white--text rounded-lg font-weight-bold"
            elevation="1"
            :loading="actionLoading"
            @click="handleStartReview"
          >
            <v-icon left size="18">mdi-play-circle-outline</v-icon>
            بدء المراجعة
          </v-btn>

          <!-- Send to Finance Button -->
          <v-btn
            v-if="refund.can_send_to_finance"
            color="#7C3AED"
            class="white--text rounded-lg font-weight-bold"
            elevation="1"
            :loading="actionLoading"
            @click="openSendToFinanceDialog"
          >
            <v-icon left size="18">mdi-send-check</v-icon>
            إرسال إلى المالية
          </v-btn>

          <!-- Reject Button -->
          <v-btn
            v-if="refund.can_reject"
            color="#DC2626"
            class="white--text rounded-lg font-weight-bold"
            elevation="1"
            :loading="actionLoading"
            @click="openRejectDialog"
          >
            <v-icon left size="18">mdi-close-circle-outline</v-icon>
            رفض الطلب
          </v-btn>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="#082C69" size="48" class="mb-3"></v-progress-circular>
        <div class="text-subtitle-1 text-muted">جاري تحميل تفاصيل طلب الاسترداد...</div>
      </div>

      <div v-else-if="!refund.id" class="text-center py-12">
        <v-icon size="64" color="#CBD5E1" class="mb-3">mdi-alert-circle-outline</v-icon>
        <div class="text-h6 font-weight-bold navy--text mb-2">طلب الاسترداد غير موجود</div>
        <v-btn color="#082C69" class="white--text rounded-lg" @click="$router.push('/customer-service/refunds')">
          العودة لقائمة الطلبات
        </v-btn>
      </div>

      <div v-else>
        <!-- Workflow Stepper Card -->
        <v-card class="elevation-1 rounded-xl pa-4 mb-6 stepper-card">
          <div class="stepper-wrapper">
            <div
              v-for="(step, idx) in steps"
              :key="idx"
              class="step-item"
              :class="{
                'step-completed': refund.step_index > step.number,
                'step-active': refund.step_index === step.number,
                'step-rejected': isRejected && refund.step_index === step.number,
              }"
            >
              <div class="step-circle">
                <v-icon v-if="refund.step_index > step.number" size="18" color="#FFFFFF">mdi-check</v-icon>
                <v-icon v-else-if="isRejected && refund.step_index === step.number" size="18" color="#FFFFFF">mdi-close</v-icon>
                <span v-else>{{ step.number }}</span>
              </div>
              <div class="step-title">{{ step.title }}</div>
              <div v-if="idx < steps.length - 1" class="step-connector"></div>
            </div>
          </div>
        </v-card>

        <!-- Status Banner if Rejected -->
        <v-alert
          v-if="isRejected"
          type="error"
          outlined
          prominent
          class="rounded-xl mb-4 bg-white"
        >
          <div class="font-weight-bold text-subtitle-1 mb-1">تم رفض طلب الاسترداد</div>
          <div><strong>سبب الرفض:</strong> {{ refund.rejection_reason || 'تم الرفض بواسطة خدمة العملاء' }}</div>
          <div v-if="refund.rejected_at" class="text-caption text-muted mt-1">تاريخ الرفض: {{ refund.rejected_at }}</div>
        </v-alert>

        <!-- Main Content 2-Column Responsive Layout -->
        <v-row>
          <!-- Left Column (Main Request Area) -->
          <v-col cols="12" lg="8">
            <!-- 1. Request Details Card -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 content-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-text-box-search-outline</v-icon>
                  <span class="text-subtitle-1 font-weight-bold navy--text">بيانات طلب الاسترداد</span>
                </div>
                <span
                  class="status-badge"
                  :style="{ backgroundColor: refund.status_color.bg, color: refund.status_color.text }"
                >
                  {{ refund.status_label }}
                </span>
              </div>

              <v-row dense class="mb-2">
                <v-col cols="12" sm="6">
                  <div class="info-block">
                    <span class="info-label">رقم الطلب</span>
                    <span class="info-value mono-code font-weight-bold navy--text">{{ refund.request_code }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-block">
                    <span class="info-label">نوع الاسترداد</span>
                    <span class="info-value font-weight-bold">{{ refund.refund_type_label }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-block">
                    <span class="info-label">المبلغ المطلوب</span>
                    <span class="info-value font-weight-bold navy--text text-subtitle-1">
                      {{ refund.requested_amount }} <small>ج.م</small>
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-block">
                    <span class="info-label">المبلغ المعتمد</span>
                    <span class="info-value font-weight-bold text-emerald text-subtitle-1">
                      {{ refund.approved_amount !== null ? refund.approved_amount + ' ج.م' : 'بانتظار قرار خدمة العملاء' }}
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-block">
                    <span class="info-label">طريقة رد المبلغ</span>
                    <span class="info-value">{{ refund.payment_method_label }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-block">
                    <span class="info-label">تاريخ ووقت التقديم</span>
                    <span class="info-value">{{ refund.submitted_at || '—' }}</span>
                  </div>
                </v-col>
              </v-row>

              <!-- Reason Box -->
              <div class="reason-box pa-3 rounded-lg mt-2">
                <div class="d-flex align-center mb-1">
                  <v-icon small color="#082C69" class="me-1">mdi-comment-question-outline</v-icon>
                  <span class="text-caption font-weight-bold navy--text">سبب طلب الاسترداد (من العميل)</span>
                </div>
                <div class="text-body-2 text-dark">{{ refund.reason }}</div>
              </div>
            </v-card>

            <!-- 2. Customer Service Decision Card (Full vs Partial) -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 decision-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-scale-balance</v-icon>
                  <span class="text-subtitle-1 font-weight-bold navy--text">قرار مراجعة خدمة العملاء</span>
                </div>
                <div>
                  <v-chip
                    v-if="refund.refund_type"
                    small
                    :color="refund.refund_type === 'full' ? '#DCFCE7' : '#FEF3C7'"
                    :text-color="refund.refund_type === 'full' ? '#15803D' : '#B45309'"
                    class="font-weight-bold"
                  >
                    <v-icon left x-small>{{ refund.refund_type === 'full' ? 'mdi-check-circle' : 'mdi-circle-half-full' }}</v-icon>
                    تم التحديد: {{ refund.refund_type_label }}
                  </v-chip>
                  <v-chip
                    v-else
                    small
                    color="#F1F5F9"
                    text-color="#64748B"
                    class="font-weight-bold"
                  >
                    <v-icon left x-small>mdi-clock-outline</v-icon>
                    بانتظار اتخاذ القرار
                  </v-chip>
                </div>
              </div>

              <!-- Interactive Decision Form (when can_save_decision) -->
              <div v-if="refund.can_save_decision">
                <p class="text-caption text-muted mb-3">
                  تحديد نوع الاسترداد (كامل أو جزئي) والمبلغ المعتمد هو اختصاص حصري لفريق خدمة العملاء بناءً على فحص الحجز ووقائع الرحلة.
                </p>

                <!-- Decision Type Selection Cards -->
                <div class="decision-types-grid mb-3">
                  <!-- Option Full -->
                  <div
                    class="decision-box cursor-pointer pa-3 rounded-xl"
                    :class="{ 'decision-box-active-full': decisionType === 'full' }"
                    @click="setDecisionType('full')"
                  >
                    <div class="d-flex align-center justify-space-between mb-1">
                      <div class="d-flex align-center">
                        <v-icon
                          :color="decisionType === 'full' ? '#10B981' : '#94A3B8'"
                          size="20"
                          class="me-2"
                        >
                          {{ decisionType === 'full' ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                        </v-icon>
                        <span class="font-weight-bold navy--text text-subtitle-2">استرداد كامل (Full)</span>
                      </div>
                      <v-chip x-small color="#ECFDF5" text-color="#059669" class="font-weight-bold">
                        100%
                      </v-chip>
                    </div>
                    <div class="text-caption text-muted ps-7">
                      استرداد كامل القيمة المدفوعة للحجز ({{ refund.requested_amount }} ج.م) لجميع المقاعد.
                    </div>
                  </div>

                  <!-- Option Partial -->
                  <div
                    class="decision-box cursor-pointer pa-3 rounded-xl"
                    :class="{ 'decision-box-active-partial': decisionType === 'partial' }"
                    @click="setDecisionType('partial')"
                  >
                    <div class="d-flex align-center justify-space-between mb-1">
                      <div class="d-flex align-center">
                        <v-icon
                          :color="decisionType === 'partial' ? '#D97706' : '#94A3B8'"
                          size="20"
                          class="me-2"
                        >
                          {{ decisionType === 'partial' ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                        </v-icon>
                        <span class="font-weight-bold navy--text text-subtitle-2">استرداد جزئي (Partial)</span>
                      </div>
                      <v-chip x-small color="#FEF3C7" text-color="#B45309" class="font-weight-bold">
                        مبلغ مخصص
                      </v-chip>
                    </div>
                    <div class="text-caption text-muted ps-7">
                      تحديد مبلغ جزئي أقل من إجمالي الحجز وفقاً للضوابط والمقاعد المشمولة.
                    </div>
                  </div>
                </div>

                <!-- Partial Amount Input -->
                <div v-if="decisionType === 'partial'" class="pa-3 rounded-xl bg-slate-light mb-3 border-dashed">
                  <v-row dense align="center">
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="decisionAmount"
                        type="number"
                        step="0.01"
                        min="0"
                        :max="refund.requested_amount"
                        label="المبلغ الجزئي المعتمد *"
                        outlined
                        dense
                        class="rounded-lg bg-white"
                        suffix="ج.م"
                        :error-messages="decisionError"
                        @input="validatePartialAmount"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <div class="text-caption text-muted">
                        الحد الأقصى المسموح للاسترداد الجزئي هو أقل من
                        <strong class="navy--text">{{ refund.requested_amount }} ج.م</strong>.
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Full Amount Summary Banner -->
                <div v-else class="pa-3 rounded-xl bg-emerald-light mb-3 d-flex align-center justify-space-between border-emerald-soft">
                  <div class="d-flex align-center">
                    <v-icon color="#059669" class="me-2" size="22">mdi-check-decagram</v-icon>
                    <div>
                      <div class="text-body-2 font-weight-bold text-emerald-dark">
                        المبلغ المعتمد للاسترداد الكامل:
                      </div>
                      <div class="text-caption text-muted">
                        سيتم اعتماد كامل قيمة الحجز المدفوعة
                      </div>
                    </div>
                  </div>
                  <span class="text-h6 font-weight-bold text-emerald-dark">
                    {{ refund.requested_amount }} <small>ج.م</small>
                  </span>
                </div>

                <!-- Action Button -->
                <div class="d-flex justify-end gap-2 mt-2">
                  <v-btn
                    color="#082C69"
                    class="white--text rounded-lg font-weight-bold px-4"
                    :loading="decisionLoading"
                    @click="submitDecision"
                  >
                    <v-icon left size="18">mdi-content-save-check-outline</v-icon>
                    حفظ قرار الاسترداد
                  </v-btn>
                </div>
              </div>

              <!-- Readonly Decision View (when past CS review) -->
              <div v-else>
                <div class="d-flex align-center justify-space-between pa-3 rounded-xl bg-slate-light">
                  <div>
                    <div class="text-caption text-muted">قرار خدمة العملاء المعتمد:</div>
                    <div class="text-subtitle-1 font-weight-bold navy--text">
                      {{ refund.refund_type_label }}
                    </div>
                  </div>
                  <div class="text-left">
                    <div class="text-caption text-muted">المبلغ المعتمد:</div>
                    <div class="text-h6 font-weight-bold text-emerald">
                      {{ refund.approved_amount !== null ? refund.approved_amount + ' ج.م' : 'قيد المراجعة' }}
                    </div>
                  </div>
                </div>
              </div>
            </v-card>

            <!-- 3. Reserved Seats / Items Table Card -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 content-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-seat-passenger</v-icon>
                  <span class="text-subtitle-1 font-weight-bold navy--text">المقاعد المشمولة في الاسترداد</span>
                </div>
                <span class="text-caption text-muted">عدد المقاعد: {{ refund.items ? refund.items.length : 0 }}</span>
              </div>

              <v-simple-table dense class="items-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-right">#</th>
                      <th class="text-right">رقم التذكرة</th>
                      <th class="text-right">رقم المقعد</th>
                      <th class="text-right">المبلغ المدفوع</th>
                      <th class="text-right">المبلغ المطلوب</th>
                      <th class="text-right">المبلغ المعتمد</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in refund.items" :key="item.id">
                      <td>{{ idx + 1 }}</td>
                      <td class="mono-code">{{ item.ticket_number }}</td>
                      <td>
                        <v-chip x-small color="#E0E7FF" text-color="#082C69" class="font-weight-bold">
                          مقعد {{ item.seat_number }}
                        </v-chip>
                      </td>
                      <td class="font-weight-medium">{{ item.paid_amount }} ج.م</td>
                      <td class="font-weight-bold navy--text">{{ item.requested_amount }} ج.م</td>
                      <td>
                        <span v-if="item.approved_amount !== null" class="font-weight-bold text-emerald">
                          {{ item.approved_amount }} ج.م
                        </span>
                        <span v-else class="text-muted">قيد المراجعة</span>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>

            <!-- 3. Attachments Card -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 content-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-paperclip</v-icon>
                  <span class="text-subtitle-1 font-weight-bold navy--text">المرفقات والوثائق</span>
                </div>
                <span class="text-caption text-muted">{{ refund.attachments ? refund.attachments.length : 0 }} مرفقات</span>
              </div>

              <div v-if="!refund.attachments || refund.attachments.length === 0" class="text-center py-4 text-muted">
                لا توجد مرفقات مع هذا الطلب
              </div>

              <v-list v-else dense class="pa-0">
                <v-list-item
                  v-for="att in refund.attachments"
                  :key="att.id"
                  class="attachment-item px-3 py-2 mb-2 rounded-lg"
                >
                  <v-list-item-avatar size="36" color="#E0E7FF" class="my-0">
                    <v-icon color="#082C69" size="20">mdi-file-document-outline</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium text-body-2">{{ att.original_name }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption text-muted">
                      {{ att.size_formatted }} • {{ att.created_at }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      small
                      outlined
                      color="#082C69"
                      class="rounded-lg font-weight-bold"
                      :href="att.download_url"
                      target="_blank"
                    >
                      <v-icon left size="16">mdi-download</v-icon>
                      تحميل
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>

            <!-- 4. Internal Notes Card -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 content-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-note-edit-outline</v-icon>
                  <span class="text-subtitle-1 font-weight-bold navy--text">الملاحظات الداخلية لفريق خدمة العملاء</span>
                </div>
                <span class="text-caption text-muted">خاصة بالموظفين فقط (لا تظهر للعميل)</span>
              </div>

              <v-textarea
                v-model="internalNotes"
                outlined
                rows="3"
                placeholder="أضف أي ملاحظات تدقيقية أو تعليمات لفريق المالية هنا..."
                class="rounded-lg"
                hide-details="auto"
              ></v-textarea>

              <div class="d-flex justify-end mt-2">
                <v-btn
                  color="#082C69"
                  class="white--text rounded-lg font-weight-bold px-4"
                  :loading="notesLoading"
                  @click="saveNotes"
                >
                  <v-icon left size="16">mdi-content-save-outline</v-icon>
                  حفظ الملاحظات
                </v-btn>
              </div>
            </v-card>

            <!-- 5. Workflow History / Timeline Card -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 content-card">
              <div class="d-flex align-center mb-3 border-bottom pb-2">
                <v-icon color="#082C69" class="me-2">mdi-history</v-icon>
                <span class="text-subtitle-1 font-weight-bold navy--text">سجل الإجراءات والمتابعة</span>
              </div>

              <div class="timeline-container">
                <div
                  v-for="(hist, idx) in refund.history"
                  :key="hist.id"
                  class="timeline-row"
                >
                  <div class="timeline-point-col">
                    <div class="timeline-dot"></div>
                    <div v-if="idx < refund.history.length - 1" class="timeline-line"></div>
                  </div>
                  <div class="timeline-body pb-3">
                    <div class="d-flex align-center justify-space-between">
                      <span class="font-weight-bold text-body-2 navy--text">
                        {{ hist.to_label || hist.action }}
                      </span>
                      <span class="text-caption text-muted">{{ hist.created_at }}</span>
                    </div>
                    <div class="text-caption text-muted mt-1">
                      بواسطة: <strong>{{ hist.actor_name }}</strong>
                    </div>
                    <div v-if="hist.note" class="timeline-note pa-2 rounded mt-1 text-caption">
                      {{ hist.note }}
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Right Column (Sidebar Summaries: Booking, Customer, Payment) -->
          <v-col cols="12" lg="4">
            <!-- 1. Booking Summary Card -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 summary-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-ticket-confirmation-outline</v-icon>
                  <span class="text-subtitle-1 font-weight-bold navy--text">ملخص الحجز</span>
                </div>
                <router-link
                  v-if="refund.booking && refund.booking.id"
                  :to="`/customer-service/bookings/${refund.booking.id}`"
                  class="text-caption text-decoration-none font-weight-bold navy--text"
                >
                  فتح الحجز ←
                </router-link>
              </div>

              <div class="summary-list">
                <div class="summary-row">
                  <span class="s-label">رقم الحجز:</span>
                  <span class="s-value mono-code font-weight-bold">{{ refund.booking.booking_ref }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">حالة الحجز:</span>
                  <span class="s-value font-weight-medium">{{ refund.booking.status_label }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">تاريخ الحجز:</span>
                  <span class="s-value">{{ refund.booking.created_at || '—' }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">تاريخ الرحلة:</span>
                  <span class="s-value font-weight-medium">{{ refund.booking.trip_date || '—' }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">رقم الرحلة:</span>
                  <span class="s-value mono-code">{{ refund.booking.trip_ref }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">اسم المسار:</span>
                  <span class="s-value">{{ refund.booking.route_name }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">نقطة الصعود:</span>
                  <span class="s-value">{{ refund.booking.start_stop }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">نقطة النزول:</span>
                  <span class="s-value">{{ refund.booking.end_stop }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">عدد المقاعد:</span>
                  <span class="s-value font-weight-bold">{{ refund.booking.seats_count }} مقاعد</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">حالة الرحلة:</span>
                  <span class="s-value">{{ refund.booking.trip_status }}</span>
                </div>
              </div>
            </v-card>

            <!-- 2. Customer Info Card -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 summary-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-account-outline</v-icon>
                  <span class="text-subtitle-1 font-weight-bold navy--text">بيانات العميل</span>
                </div>
                <router-link
                  v-if="refund.customer && refund.customer.id"
                  :to="`/customer-service/customers/${refund.customer.id}`"
                  class="text-caption text-decoration-none font-weight-bold navy--text"
                >
                  الملف ←
                </router-link>
              </div>

              <div v-if="refund.customer" class="summary-list">
                <div class="summary-row">
                  <span class="s-label">رقم العميل:</span>
                  <span class="s-value mono-code">{{ refund.customer.customer_ref }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">الاسم:</span>
                  <span class="s-value font-weight-bold navy--text">{{ refund.customer.name }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">الهاتف:</span>
                  <span class="s-value dir-ltr text-right">{{ refund.customer.phone }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">البريد:</span>
                  <span class="s-value dir-ltr text-right text-caption">{{ refund.customer.email }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">رصيد المحفظة:</span>
                  <span class="s-value font-weight-bold text-sky">{{ refund.customer.wallet }} ج.م</span>
                </div>
              </div>
            </v-card>

            <!-- 3. Payment Info Card -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 summary-card">
              <div class="d-flex align-center mb-3 border-bottom pb-2">
                <v-icon color="#082C69" class="me-2">mdi-credit-card-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold navy--text">بيانات الدفع</span>
              </div>

              <div v-if="refund.payment" class="summary-list">
                <div class="summary-row">
                  <span class="s-label">المبلغ الإجمالي:</span>
                  <span class="s-value font-weight-bold">{{ refund.payment.total_amount }} ج.م</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">المبلغ المدفوع:</span>
                  <span class="s-value font-weight-bold text-emerald">{{ refund.payment.paid_amount }} ج.م</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">طريقة الدفع:</span>
                  <span class="s-value font-weight-medium">{{ refund.payment.payment_method_label }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">تاريخ الدفع:</span>
                  <span class="s-value">{{ refund.payment.payment_date || '—' }}</span>
                </div>
                <div class="summary-row">
                  <span class="s-label">حالة الدفع:</span>
                  <span class="s-value text-emerald font-weight-bold">{{ refund.payment.payment_status_label }}</span>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- Send To Finance Modal -->
      <v-dialog v-model="sendToFinanceDialog" max-width="520" persistent>
        <v-card class="rounded-xl pa-4">
          <div class="d-flex align-center mb-3">
            <v-icon color="#7C3AED" class="me-2">mdi-send-check</v-icon>
            <span class="text-h6 font-weight-bold navy--text">إرسال الطلب إلى الإدارة المالية</span>
          </div>
          <p class="text-body-2 text-muted mb-3">
            سيتم تحويل طلب الاسترداد إلى الإدارة المالية مع اعتماد قرار خدمة العملاء الموضح أدناه.
          </p>

          <!-- Decision Summary in Dialog -->
          <div class="decision-dialog-box pa-3 rounded-xl mb-3">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-caption font-weight-bold text-muted">نوع الاسترداد المعتمد:</span>
              <v-chip
                small
                :color="currentDecisionType === 'full' ? '#DCFCE7' : '#FEF3C7'"
                :text-color="currentDecisionType === 'full' ? '#15803D' : '#B45309'"
                class="font-weight-bold"
              >
                {{ currentDecisionType === 'full' ? 'استرداد كامل (100%)' : 'استرداد جزئي' }}
              </v-chip>
            </div>
            <div class="d-flex align-center justify-space-between">
              <span class="text-caption font-weight-bold text-muted">المبلغ المعتمد للإرسال:</span>
              <span class="font-weight-bold text-emerald text-subtitle-1">
                {{ currentDecisionAmount }} ج.م
              </span>
            </div>
          </div>

          <v-textarea
            v-model="financeNote"
            label="ملاحظات توجيه للمالية (اختياري)"
            outlined
            rows="3"
            class="rounded-lg"
            placeholder="اكتب أي ملاحظة للإدارة المالية هنا..."
          ></v-textarea>
          <div class="d-flex justify-end gap-2 mt-3">
            <v-btn outlined color="#64748B" class="rounded-lg font-weight-bold" @click="sendToFinanceDialog = false">
              إلغاء
            </v-btn>
            <v-btn
              color="#7C3AED"
              class="white--text rounded-lg font-weight-bold px-4"
              :loading="actionLoading"
              @click="submitSendToFinance"
            >
              تأكيد الإرسال للمالية
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- Reject Request Modal -->
      <v-dialog v-model="rejectDialog" max-width="500" persistent>
        <v-card class="rounded-xl pa-4">
          <div class="d-flex align-center mb-3">
            <v-icon color="#DC2626" class="me-2">mdi-alert-circle-outline</v-icon>
            <span class="text-h6 font-weight-bold navy--text">رفض طلب الاسترداد</span>
          </div>
          <p class="text-body-2 text-muted mb-3">
            يرجى تحديد سبب الرفض بدقة. سيتم إشعار العميل بهذا السبب ورفض الطلب نهائياً.
          </p>
          <v-textarea
            v-model="rejectionReason"
            label="سبب الرفض (إلزامي)"
            outlined
            rows="3"
            class="rounded-lg mb-2"
            placeholder="مثال: الحجز تم استخدامه بالكامل ولم يتم الإبلاغ عن أي عطل..."
            :error-messages="rejectError"
          ></v-textarea>
          <v-textarea
            v-model="rejectInternalNote"
            label="ملاحظة داخلية إضافية (اختياري)"
            outlined
            rows="2"
            class="rounded-lg"
            placeholder="ملاحظات إضافية للملف الداخلي فقط..."
          ></v-textarea>
          <div class="d-flex justify-end gap-2 mt-3">
            <v-btn outlined color="#64748B" class="rounded-lg font-weight-bold" @click="rejectDialog = false">
              إلغاء
            </v-btn>
            <v-btn
              color="#DC2626"
              class="white--text rounded-lg font-weight-bold px-4"
              :loading="actionLoading"
              @click="submitReject"
            >
              تأكيد الرفض
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- Snackbar for notifications -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">إغلاق</v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'CustomerServiceRefundView',
  data() {
    return {
      loading: true,
      actionLoading: false,
      notesLoading: false,
      decisionLoading: false,
      refund: {},
      decisionType: 'full',
      decisionAmount: '',
      decisionError: '',
      internalNotes: '',
      sendToFinanceDialog: false,
      financeNote: '',
      rejectDialog: false,
      rejectionReason: '',
      rejectInternalNote: '',
      rejectError: '',
      snackbar: {
        show: false,
        text: '',
        color: 'success',
      },
      steps: [
        { number: 1, title: 'تقديم الطلب' },
        { number: 2, title: 'مراجعة خدمة العملاء' },
        { number: 3, title: 'إرسال إلى المالية' },
        { number: 4, title: 'قرار المالية' },
        { number: 5, title: 'إبلاغ العميل' },
      ],
    };
  },
  computed: {
    isRejected() {
      return (
        this.refund.status === 'rejected_by_customer_service' ||
        this.refund.status === 'finance_rejected'
      );
    },
    currentDecisionType() {
      return this.refund.refund_type || this.decisionType || 'full';
    },
    currentDecisionAmount() {
      if (this.currentDecisionType === 'full') {
        return this.refund.requested_amount || '0.00';
      }
      return this.refund.approved_amount !== null
        ? this.refund.approved_amount
        : (this.decisionAmount || this.refund.requested_amount || '0.00');
    },
  },
  created() {
    this.fetchDetail();
  },
  methods: {
    async fetchDetail() {
      this.loading = true;
      try {
        const id = this.$route.params.id;
        const response = await axios.get(`/customer-service/refunds/${id}`);
        if (response.data && response.data.data) {
          this.refund = response.data.data;
          this.internalNotes = this.refund.internal_notes || '';
          if (this.refund.refund_type) {
            this.decisionType = this.refund.refund_type;
            this.decisionAmount = this.refund.approved_amount !== null ? this.refund.approved_amount : this.refund.requested_amount;
          } else {
            this.decisionType = 'full';
            this.decisionAmount = this.refund.requested_amount || '';
          }
        }
      } catch (err) {
        console.error('Error fetching refund detail:', err);
        this.showSnackbar('فشل في تحميل تفاصيل طلب الاسترداد', 'error');
      } finally {
        this.loading = false;
      }
    },
    setDecisionType(type) {
      this.decisionType = type;
      this.decisionError = '';
      if (type === 'full') {
        this.decisionAmount = this.refund.requested_amount;
      }
    },
    validatePartialAmount() {
      const maxAmt = parseFloat(this.refund.requested_amount) || 0;
      const amt = parseFloat(this.decisionAmount);
      if (isNaN(amt) || amt < 0) {
        this.decisionError = 'يرجى إدخال مبلغ صحيح.';
        return false;
      }
      if (amt >= maxAmt) {
        this.decisionError = `المبلغ الجزئي يجب أن يكون أقل من إجمالي المبلغ المطلوب (${maxAmt} ج.م). للاسترداد الكامل اختر "استرداد كامل".`;
        return false;
      }
      if (amt <= 0) {
        this.decisionError = 'المبلغ المعتمد يجب أن يكون أكبر من 0.';
        return false;
      }
      this.decisionError = '';
      return true;
    },
    async submitDecision() {
      const maxAmt = parseFloat(this.refund.requested_amount) || 0;
      let amt = maxAmt;

      if (this.decisionType === 'partial') {
        if (!this.validatePartialAmount()) {
          return;
        }
        amt = parseFloat(this.decisionAmount);
      } else {
        this.decisionAmount = maxAmt;
        amt = maxAmt;
      }

      this.decisionLoading = true;
      try {
        const id = this.refund.id;
        const res = await axios.post(`/customer-service/refunds/${id}/decision`, {
          refund_type: this.decisionType,
          approved_amount: amt,
        });
        if (res.data && res.data.success) {
          this.refund = res.data.data;
          this.decisionType = this.refund.refund_type || 'full';
          this.decisionAmount = this.refund.approved_amount !== null ? this.refund.approved_amount : this.refund.requested_amount;
          this.showSnackbar('تم حفظ قرار الاسترداد بنجاح', 'success');
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'فشل في حفظ القرار';
        this.decisionError = msg;
        this.showSnackbar(msg, 'error');
      } finally {
        this.decisionLoading = false;
      }
    },
    async handleStartReview() {
      this.actionLoading = true;
      try {
        const id = this.refund.id;
        const res = await axios.post(`/customer-service/refunds/${id}/start-review`);
        if (res.data && res.data.success) {
          this.refund = res.data.data;
          this.showSnackbar('تم بدء مراجعة الطلب بنجاح', 'success');
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'حدث خطأ أثناء بدء المراجعة';
        this.showSnackbar(msg, 'error');
      } finally {
        this.actionLoading = false;
      }
    },
    openSendToFinanceDialog() {
      this.financeNote = '';
      this.sendToFinanceDialog = true;
    },
    async submitSendToFinance() {
      const maxAmt = parseFloat(this.refund.requested_amount) || 0;
      let finalType = this.refund.refund_type || this.decisionType || 'full';
      let finalAmt = this.refund.approved_amount !== null
        ? parseFloat(this.refund.approved_amount)
        : (finalType === 'full' ? maxAmt : parseFloat(this.decisionAmount));

      if (finalType === 'full') {
        finalAmt = maxAmt;
      }

      if (finalType === 'partial' && (isNaN(finalAmt) || finalAmt <= 0 || finalAmt >= maxAmt)) {
        this.showSnackbar(`يرجى تحديد مبلغ جزئي صحيح أقل من ${maxAmt} ج.م قبل الإرسال للمالية`, 'error');
        return;
      }

      this.actionLoading = true;
      try {
        const id = this.refund.id;
        const res = await axios.post(`/customer-service/refunds/${id}/send-to-finance`, {
          note: this.financeNote,
          refund_type: finalType,
          approved_amount: finalAmt,
        });
        if (res.data && res.data.success) {
          this.refund = res.data.data;
          this.decisionType = this.refund.refund_type || 'full';
          this.decisionAmount = this.refund.approved_amount;
          this.sendToFinanceDialog = false;
          this.showSnackbar('تم إرسال الطلب إلى الإدارة المالية بنجاح', 'success');
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'حدث خطأ أثناء إرسال الطلب للمالية';
        this.showSnackbar(msg, 'error');
      } finally {
        this.actionLoading = false;
      }
    },
    openRejectDialog() {
      this.rejectionReason = '';
      this.rejectInternalNote = '';
      this.rejectError = '';
      this.rejectDialog = true;
    },
    async submitReject() {
      if (!this.rejectionReason || this.rejectionReason.trim().length < 3) {
        this.rejectError = 'سبب الرفض إلزامي ويجب ألا يقل عن 3 أحرف';
        return;
      }
      this.rejectError = '';
      this.actionLoading = true;
      try {
        const id = this.refund.id;
        const res = await axios.post(`/customer-service/refunds/${id}/reject`, {
          rejection_reason: this.rejectionReason.trim(),
          internal_note: this.rejectInternalNote.trim(),
        });
        if (res.data && res.data.success) {
          this.refund = res.data.data;
          this.rejectDialog = false;
          this.showSnackbar('تم رفض طلب الاسترداد بنجاح', 'success');
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'حدث خطأ أثناء رفض الطلب';
        this.showSnackbar(msg, 'error');
      } finally {
        this.actionLoading = false;
      }
    },
    async saveNotes() {
      this.notesLoading = true;
      try {
        const id = this.refund.id;
        const res = await axios.post(`/customer-service/refunds/${id}/notes`, {
          notes: this.internalNotes,
        });
        if (res.data && res.data.success) {
          this.showSnackbar('تم حفظ الملاحظات الداخلية بنجاح', 'success');
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'فشل في حفظ الملاحظات';
        this.showSnackbar(msg, 'error');
      } finally {
        this.notesLoading = false;
      }
    },
    showSnackbar(text, color = 'success') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
  },
};
</script>

<style scoped>
.refund-view-page {
  background-color: #F8FAFC;
  min-height: 100vh;
  direction: rtl;
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

.navy--text {
  color: #082C69 !important;
}

.text-muted {
  color: #64748B !important;
}

.text-sky {
  color: #0369A1 !important;
}

.text-emerald {
  color: #059669 !important;
}

.breadcrumb-link {
  cursor: pointer;
  transition: color 0.2s;
}
.breadcrumb-link:hover {
  color: #082C69;
}

.mono-code {
  font-family: monospace, sans-serif;
  letter-spacing: 0.5px;
}

/* Stepper styles */
.stepper-card {
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
}

.stepper-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow-x: auto;
  padding: 8px 16px;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 120px;
}

.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #E2E8F0;
  color: #64748B;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  margin-bottom: 8px;
  z-index: 2;
  transition: all 0.3s;
}

.step-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748B;
  text-align: center;
}

.step-connector {
  position: absolute;
  top: 18px;
  right: calc(50% + 18px);
  width: calc(100% - 36px);
  height: 3px;
  background: #E2E8F0;
  z-index: 1;
}

.step-completed .step-circle {
  background: #10B981;
  color: #FFFFFF;
}

.step-completed .step-title {
  color: #10B981;
}

.step-completed .step-connector {
  background: #10B981;
}

.step-active .step-circle {
  background: #082C69;
  color: #FFFFFF;
  box-shadow: 0 0 0 4px #DBEAFE;
}

.step-active .step-title {
  color: #082C69;
  font-weight: 700;
}

.step-rejected .step-circle {
  background: #DC2626 !important;
  color: #FFFFFF !important;
  box-shadow: 0 0 0 4px #FEE2E2 !important;
}

.step-rejected .step-title {
  color: #DC2626 !important;
}

/* Cards & Summary */
.content-card, .summary-card {
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
}

.border-bottom {
  border-bottom: 1px solid #E2E8F0;
}

.info-block {
  display: flex;
  flex-direction: column;
  padding: 6px 0;
}

.info-label {
  font-size: 0.75rem;
  color: #64748B;
  margin-bottom: 2px;
}

.info-value {
  font-size: 0.9rem;
  color: #1E293B;
}

.reason-box {
  background-color: #F1F5F9;
  border-right: 4px solid #082C69;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
}

.summary-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.85rem;
  padding: 4px 0;
  border-bottom: 1px dashed #F1F5F9;
}

.summary-row:last-child {
  border-bottom: none;
}

.s-label {
  color: #64748B;
}

.s-value {
  color: #0F172A;
  text-align: left;
}

.attachment-item {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
}

/* Timeline */
.timeline-container {
  padding: 4px 0;
}

.timeline-row {
  display: flex;
  gap: 12px;
}

.timeline-point-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 20px;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #082C69;
  margin-top: 4px;
}

.timeline-line {
  flex: 1;
  width: 2px;
  background: #CBD5E1;
  margin: 4px 0;
}

.timeline-body {
  flex: 1;
}

.timeline-note {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  color: #475569;
}

.gap-2 {
  gap: 8px;
}

.gap-1 {
  gap: 4px;
}

.dir-ltr {
  direction: ltr;
}

/* Decision Card styles */
.decision-card {
  border: 1.5px solid #CBD5E1 !important;
  background: #FFFFFF;
}

.decision-types-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (max-width: 600px) {
  .decision-types-grid {
    grid-template-columns: 1fr;
  }
}

.decision-box {
  border: 2px solid #E2E8F0;
  background: #FFFFFF;
  transition: all 0.2s ease;
}

.decision-box:hover {
  border-color: #94A3B8;
}

.decision-box-active-full {
  border-color: #10B981 !important;
  background: #F0FDF4 !important;
  box-shadow: 0 0 0 1px #10B981;
}

.decision-box-active-partial {
  border-color: #D97706 !important;
  background: #FFFBEB !important;
  box-shadow: 0 0 0 1px #D97706;
}

.border-dashed {
  border: 1px dashed #CBD5E1;
}

.border-emerald-soft {
  border: 1px solid #A7F3D0;
}

.bg-slate-light {
  background-color: #F8FAFC !important;
}

.bg-emerald-light {
  background-color: #ECFDF5 !important;
}

.text-emerald-dark {
  color: #065F46 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.decision-dialog-box {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
}
</style>
